import React, { useMemo } from 'react'
import { EditorColoreApiType } from '@utils/types'
import FormSelect from '../FormSelect/FormSelect'
import { normalizeHexColor, splitHexColorAndAlpha } from '../../../utils/editor-bozze'
import styles from './PantoneSelect.module.sass'
import { useEditorContext } from 'context/EditorContext'
import { getPantoneHex } from '@utils/editor-bozze/colors'

type PantoneSelectValueMode = 'code' | 'hex'

interface PantoneSelectChangeEvent {
  target: {
    type: string
    name: string
    value: string
    label: string
  }
}

interface PantoneOption {
  label: string
  value: string
  hex: string
  searchText: string
}

interface PantoneApiItem extends EditorColoreApiType {}

interface PantoneSelectProps {
  id?: string
  name?: string
  value?: string
  onChange?: (event: PantoneSelectChangeEvent) => void
  onBlur?: (...args: any[]) => void
  placeholder?: string
  className?: string
  isDisabled?: boolean
  isClearable?: boolean
  showSwatchInValue?: boolean
  valueMode?: PantoneSelectValueMode
  initialPantoni?: PantoneApiItem[]
  allowedColors?: EditorColoreApiType[]
  fixedMenu?: boolean
}

const normalizeSearchValue = (value = '') => value.toLowerCase().trim()

const mapPantoneToOption = (pantone: PantoneApiItem, valueMode: PantoneSelectValueMode) => {
  const hex = normalizeHexColor(pantone?.hex || '')
  if (!pantone?.codice || !hex) return null

  return {
    label: pantone.codice,
    value: valueMode === 'hex' ? hex : pantone.codice,
    hex,
    searchText: normalizeSearchValue(
      [pantone.codice, pantone.nome || ''].filter(Boolean).join(' ')
    ),
  }
}

const pantoneSelectStyles = {
  menuPortal: (base: Record<string, any>) => ({
    ...base,
    zIndex: 1000000,
  }),
  menu: (base: Record<string, any>) => ({
    ...base,
    zIndex: 1000000,
  }),
}

const PantoneSelect = ({
  id = '',
  name = '',
  value = '',
  onChange = () => {},
  onBlur = () => {},
  placeholder = '',
  className = '',
  isDisabled = false,
  isClearable = false,
  showSwatchInValue = false,
  valueMode = 'code',
  initialPantoni = [],
  allowedColors = [],
  fixedMenu = false,
}: PantoneSelectProps) => {
  const editorContext = useEditorContext()
  const pantoniFromContext = (editorContext?.pantoni || []) as PantoneApiItem[]

  const pantoni = useMemo(() => {
    return initialPantoni.length ? initialPantoni : pantoniFromContext
  }, [initialPantoni, pantoniFromContext])

  const normalizedAllowedColors = useMemo(
    () => (Array.isArray(allowedColors) ? allowedColors.filter(Boolean) : []),
    [allowedColors]
  )

  const options = useMemo<PantoneOption[]>(() => {
    const pantoniAllowed = normalizedAllowedColors?.map((color) => getPantoneHex(color.hex)) || []

    const mappedOptions = pantoni
      .filter((pantone) =>
        pantoniAllowed.length ? pantoniAllowed.includes(normalizeHexColor(pantone.hex)) : true
      )
      .map((pantone) => mapPantoneToOption(pantone, valueMode))
      .filter(Boolean) as PantoneOption[]

    return Array.from(new Map(mappedOptions.map((option) => [option.value, option])).values())
  }, [valueMode, normalizedAllowedColors, pantoni])

  const filterPantoniOption = ({ data }: { data: PantoneOption }, inputValue: string): boolean => {
    const normalizedInput = normalizeSearchValue(inputValue)

    if (!normalizedInput) return true
    return data.searchText.includes(normalizedInput)
  }

  const formatPantoneOptionLabel = (
    { label, hex }: PantoneOption,
    { context }: { context: string }
  ) => {
    const { alphaHex, hasAlpha } = splitHexColorAndAlpha(hex)
    const isTransparent = hasAlpha && alphaHex !== 'ff'
    return (
      <div className={styles.option}>
        {(showSwatchInValue || context !== 'value') && (
          <div
            className={`${styles.swatch} ${isTransparent ? styles['swatch--transparent'] : ''}`}
            style={{
              backgroundColor: hex,
            }}
          />
        )}
        <span>{label}</span>
      </div>
    )
  }

  return (
    <FormSelect
      id={id}
      name={name}
      value={value}
      options={options}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      className={`${styles.root} ${
        showSwatchInValue ? styles.rootWithSwatchInValue : ''
      } ${className}`}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isSearchable={true}
      formatOptionLabel={formatPantoneOptionLabel}
      filterOption={filterPantoniOption}
      menuPortalTarget={fixedMenu && typeof document !== 'undefined' ? document.body : undefined}
      menuPosition={fixedMenu ? 'fixed' : 'absolute'}
      minMenuHeight={250}
      styles={pantoneSelectStyles}
    />
  )
}

export default PantoneSelect
